import Icon from 'components/shared/Icon/Icon';

const STATUS_CONFIG = {
  initiating: {
    title: 'Connecting to the card reader',
    subtitle: 'This will only take a moment…',
    showAmount: true
  },
  ready: {
    title: 'Tap, insert, or swipe your card',
    subtitle: 'Or enter your card details manually below.',
    showAmount: true
  },
  authorizing: {
    title: 'Authorizing your payment',
    subtitle: 'Hang tight — this only takes a few seconds.',
    showAmount: true
  },
  success: {
    title: 'Payment approved',
    subtitle: "Thank you! You're all set.",
    showAmount: true
  },
  canceled: {
    title: 'Payment canceled',
    subtitle: 'You can try again, or choose another way to pay.',
    showAmount: false
  },
  error: {
    title: "Payment didn't go through",
    subtitle: 'Please try again, or enter your card details manually.',
    showAmount: false
  }
};

const ReaderVisual = ({ status }) => {
  if (status === 'success')
    return (
      <div className="flex h-[150px] w-[150px] animate-check-bounce items-center justify-center rounded-full bg-gradient-to-br from-success-400 to-success-600 shadow-[0_18px_40px_-12px_rgba(9,146,80,0.6)] ring-8 ring-success-50">
        <Icon icon="check-thick-linear" color="white" size={72} />
      </div>
    );

  if (status === 'error' || status === 'canceled')
    return <Icon icon="illustration-payment-failed" size={172} className="animate-enter-up" />;

  if (status === 'authorizing')
    return (
      <>
        <span className="absolute inset-0 rounded-full bg-primary-50" />
        <span className="absolute inset-0 animate-spin rounded-full border-[6px] border-solid border-primary-100 border-t-primary-500" />
        <Icon icon="illustration-card-pay" size={96} className="relative animate-breathe" />
      </>
    );

  if (status === 'ready')
    return (
      <>
        <span className="absolute inset-0 animate-breathe rounded-full bg-primary-100" />
        <span className="absolute inset-5 animate-ping rounded-full border-2 border-solid border-primary-300 opacity-70" />
        <span className="absolute inset-5 animate-ping rounded-full border-2 border-solid border-primary-200 opacity-50 [animation-delay:0.7s]" />
        <Icon icon="illustration-card-pay" size={124} className="relative" />
      </>
    );

  // initiating
  return (
    <>
      <span className="absolute inset-0 rounded-full bg-primary-50" />
      <span className="absolute inset-1 animate-spin rounded-full border-4 border-solid border-primary-100 border-t-primary-500" />
      <Icon icon="pos-mono" color="primary" size={84} className="relative" />
    </>
  );
};

const ReaderStatus = ({ status = 'initiating', amount, message }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.initiating;
  // Prefer the live terminal prompt for tap/error states.
  const title = (status === 'ready' || status === 'error') && message ? message : config.title;

  return (
    <div className="flex w-full flex-col items-center gap-7 xs:gap-5">
      <div key={status} className="relative flex h-[210px] w-[210px] items-center justify-center">
        <ReaderVisual status={status} />
      </div>

      {config.showAmount && amount && (
        <div className="text-6xl font-700 leading-none tracking-tight text-primary-900 sm:text-5xl xs:text-4xl">
          {amount}
        </div>
      )}

      <div
        key={`copy-${status}`}
        className="flex max-w-[460px] animate-enter-up flex-col items-center gap-2 text-center">
        <p className="text-3xl font-600 leading-tight text-neutral-900 sm:text-2xl xs:text-xl">
          {title}
        </p>
        {config.subtitle && (
          <p className="text-lg text-neutral-600 sm:text-base">{config.subtitle}</p>
        )}
      </div>
    </div>
  );
};

export default ReaderStatus;
